// Settings → Assistant (ERP-130): the company's API key and model, whether people may use their
// own key, and which roles may use the assistant at all. Keys go to the server and never come
// back: only their last four characters are shown. Also what the assistant used each month, and
// the monthly limits and prices that go with it (ERP-133).
import { api, can as grants, t, useAction, useApi, useAuth } from "@keel/core";
import { DataTable, Field, Select, Skeleton, Switch } from "@keel/ui";
import { useState } from "react";
import { Link } from "react-router-dom";

interface Settings {
  hasKey: boolean;
  keyHint: string | null;
  model: string | null;
  defaultModel: string;
  models: { id: string; label: string }[];
  allowPersonalKeys: boolean;
  serverKey: boolean;
  personalKeys: number;
  updatedByName: string | null;
  updatedAt: string | null;
  warning: string | null;
  monthlyLimit: number | null;
  monthlyLimitPerPerson: number | null;
  prices: Record<string, { input: number; output: number }>;
  priceCurrency: string;
}
interface Grant {
  key: string;
  conditions?: unknown;
}
interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  grants: Grant[];
  isSystem: boolean;
  users: number;
}

const CHAT = "assistant.chat.write";
/** Grants that let a role use the assistant, other than `*`. */
const GIVES_CHAT = [
  "assistant.*",
  "assistant.manage",
  "assistant.write",
  "assistant.chat.*",
  "assistant.chat.manage",
  CHAT,
];

export function AssistantSettingsTab() {
  const { run, busy } = useAction();
  const { can } = useAuth();
  const s = useApi<Settings>("/assistant/settings").data;
  const roles = useApi<Role[]>(can("platform.roles.read") ? "/roles" : null).data;
  const [key, setKey] = useState("");
  const [replacing, setReplacing] = useState(false);
  if (!s) return <Skeleton />;

  const save = (body: Record<string, unknown>, done: string) =>
    run(
      () => api<Settings>("/assistant/settings", { method: "PUT", body }),
      (r) => r.warning ?? done,
    );
  const saveKey = async () => {
    const r = await save({ apiKey: key }, t("Company key saved"));
    if (r) {
      setKey("");
      setReplacing(false);
    }
  };

  // Switch a role on or off, keeping everything else it grants. Taking it away replaces a
  // module-wide grant with the one that remains (managing the assistant).
  const toggle = (role: Role, on: boolean) => {
    let next = role.grants;
    if (on) next = [...next, { key: CHAT }];
    else {
      const wide = next.some((g) => g.key === "assistant.*" || g.key === "assistant.manage");
      next = next.filter((g) => !GIVES_CHAT.includes(g.key));
      if (wide) next = [...next, { key: "assistant.settings.manage" }];
    }
    return run(
      () =>
        api(`/roles/${role.id}`, {
          method: "PUT",
          body: { name: role.name, description: role.description, grants: next },
        }),
      on
        ? t("{role} may use the assistant", { role: role.name })
        : t("{role} can no longer use the assistant", { role: role.name }),
    );
  };

  return (
    <div className="stack">
      <section className="card">
        <header className="card-head">
          <div>
            <h2>{t("Company API key")}</h2>
            <p className="sub">
              {t(
                "The assistant runs on Claude, by Anthropic. The company's key is used for everyone without a key of their own.",
              )}
            </p>
          </div>
        </header>
        <div className="card-body stack">
          {s.hasKey && !replacing ? (
            <div className="row-between">
              <span>
                {t("In place")}: <span className="inline-code">{s.keyHint}</span>
                {s.updatedByName && (
                  <span className="muted sm">
                    {" "}
                    · {s.updatedByName}
                    {s.updatedAt && `, ${new Date(s.updatedAt).toLocaleDateString()}`}
                  </span>
                )}
              </span>
              <span className="row" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm" onClick={() => setReplacing(true)}>
                  {t("Replace")}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  disabled={busy}
                  onClick={() => save({ apiKey: "" }, t("Company key removed"))}
                >
                  {t("Remove")}
                </button>
              </span>
            </div>
          ) : (
            <form
              className="row"
              style={{ gap: 8, alignItems: "flex-end" }}
              onSubmit={(e) => {
                e.preventDefault();
                saveKey();
              }}
            >
              <div style={{ flex: 1 }}>
                <Field
                  label={t("Anthropic API key")}
                  tip={t(
                    "Create one in the Anthropic console (console.anthropic.com → API keys); usage is billed to that account. It is checked with Anthropic before it is saved, stored encrypted, and never shown again.",
                  )}
                  hint={
                    s.serverKey
                      ? t("Without one, the installation's own key is used.")
                      : t("Without one, only people with a key of their own can use the assistant.")
                  }
                >
                  <input
                    type="password"
                    autoComplete="off"
                    placeholder="sk-ant-…"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                  />
                </Field>
              </div>
              <button type="submit" className="btn btn-primary" disabled={busy || !key.trim()}>
                {busy ? t("Checking…") : t("Save key")}
              </button>
              {replacing && (
                <button type="button" className="btn" onClick={() => setReplacing(false)}>
                  {t("Cancel")}
                </button>
              )}
            </form>
          )}

          <Field
            label={t("Model")}
            tip={t(
              "Which Claude answers. The most capable is best at multi-step requests; the faster ones cost less per question.",
            )}
          >
            <Select
              value={s.model ?? ""}
              onChange={(e) => save({ model: e.target.value }, t("Model saved"))}
              disabled={busy}
            >
              <option value="">
                {t("Installation default")} ({s.defaultModel})
              </option>
              {s.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </Select>
          </Field>

          <Switch
            checked={s.allowPersonalKeys}
            onChange={(v) =>
              save({ allowPersonalKeys: v }, v ? t("Personal keys allowed") : t("Personal keys switched off"))
            }
            label={t("People may use their own key")}
            hint={
              s.allowPersonalKeys
                ? s.personalKeys === 1
                  ? t("1 person uses their own key; theirs comes before the company's.")
                  : t("{n} people use their own key; theirs comes before the company's.", {
                      n: s.personalKeys,
                    })
                : t("Everyone uses the company's key; keys people added are kept but not used.")
            }
          />
        </div>
      </section>

      <section className="card table-card">
        <header className="card-head">
          <div>
            <h2>{t("Who can use the assistant")}</h2>
            <p className="sub">
              {t("By role. The assistant can only read and change what each person's role allows anyway.")}{" "}
              <Link className="assistant-link" to="/settings?tab=roles">
                {t("All roles")}
              </Link>
            </p>
          </div>
        </header>
        {!can("platform.roles.read") ? (
          <p className="card-body muted">{t("You need the right to view roles to see who can use it.")}</p>
        ) : !roles ? (
          <Skeleton rows={3} />
        ) : (
          <DataTable
            rows={roles}
            columns={[
              {
                key: "n",
                label: t("Role"),
                render: (r) => (
                  <span>
                    {r.name}
                    {r.description && <span className="muted sm"> · {r.description}</span>}
                  </span>
                ),
              },
              { key: "u", label: t("People"), align: "right", render: (r) => r.users },
              {
                key: "a",
                label: t("Uses the assistant"),
                render: (r) =>
                  r.permissions.includes("*") ? (
                    <span className="muted sm">{t("Always (full access)")}</span>
                  ) : (
                    <Switch
                      checked={grants(r.permissions, CHAT)}
                      onChange={(v) => toggle(r, v)}
                      ariaLabel={t("{role} uses the assistant", { role: r.name })}
                    />
                  ),
              },
            ]}
          />
        )}
        {roles && !can("platform.roles.write") && (
          <p className="card-body muted sm">{t("Changing a role needs the right to edit roles.")}</p>
        )}
      </section>

      <UsageCard settings={s} save={save} busy={busy} />
    </div>
  );
}

interface PersonUsage {
  userId: string;
  name: string;
  email: string;
  conversations: number;
  inputTokens: number;
  outputTokens: number;
  limitedTokens: number;
  personalTokens: number;
  cost: number | null;
  ownLimit: number | null;
  limit: number | null;
}
interface UsageReport {
  month: string;
  inputTokens: number;
  outputTokens: number;
  limitedTokens: number;
  cost: number | null;
  currency: string;
  monthlyLimit: number | null;
  monthlyLimitPerPerson: number | null;
  people: PersonUsage[];
  models: { model: string; inputTokens: number; outputTokens: number; cost: number | null }[];
}

const n = (v: number) => v.toLocaleString();
const shift = (month: string, by: number) => {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
/** Empty is no limit; otherwise a whole number of tokens. */
const limitOf = (v: string) => (v.trim() ? Math.max(0, Math.round(Number(v.replace(/\s/g, "")))) : 0);

/** What the assistant used in a month, by person and model, and the limits and prices (ERP-133). */
function UsageCard({
  settings: s,
  save,
  busy,
}: {
  settings: Settings;
  save: (body: Record<string, unknown>, done: string) => Promise<unknown>;
  busy: boolean;
}) {
  const { run } = useAction();
  const [month, setMonth] = useState<string | null>(null);
  const u = useApi<UsageReport>(`/assistant/usage${month ? `?month=${month}` : ""}`).data;
  const [company, setCompany] = useState(s.monthlyLimit ? String(s.monthlyLimit) : "");
  const [perPerson, setPerPerson] = useState(s.monthlyLimitPerPerson ? String(s.monthlyLimitPerPerson) : "");
  const [prices, setPrices] = useState(() =>
    Object.fromEntries(
      s.models.map((m) => [
        m.id,
        { input: String(s.prices[m.id]?.input ?? ""), output: String(s.prices[m.id]?.output ?? "") },
      ]),
    ),
  );
  const [currency, setCurrency] = useState(s.priceCurrency);
  const [own, setOwn] = useState<Record<string, string>>({});
  const money = (v: number | null) =>
    v === null
      ? "—"
      : `${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${u?.currency ?? currency}`;
  const priced = Object.values(s.prices).length > 0;

  const saveLimits = () =>
    save({ monthlyLimit: limitOf(company), monthlyLimitPerPerson: limitOf(perPerson) }, t("Limits saved"));
  const savePrices = () => {
    const out: Record<string, { input: number; output: number }> = {};
    for (const [model, p] of Object.entries(prices)) {
      if (p.input.trim() === "" && p.output.trim() === "") continue;
      out[model] = { input: Number(p.input || 0), output: Number(p.output || 0) };
    }
    return save({ prices: out, priceCurrency: currency }, t("Prices saved"));
  };
  const setLimit = (p: PersonUsage, value: number | null) =>
    run(
      () => api(`/assistant/limits/${p.userId}`, { method: "PUT", body: { monthlyLimit: value } }),
      value
        ? t("{name}'s limit saved", { name: p.name })
        : t("{name} is back on the default", { name: p.name }),
    );

  return (
    <section className="card table-card">
      <header className="card-head">
        <div>
          <h2>{t("Usage and limits")}</h2>
          <p className="sub">
            {t(
              "Tokens are what Anthropic counts and bills. Limits cover the company's and the installation's key; a key of someone's own is theirs.",
            )}
          </p>
        </div>
        <div className="row" style={{ gap: 4 }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setMonth(shift(u?.month ?? "", -1))}
            aria-label={t("Previous month")}
          >
            ‹
          </button>
          <span className="assistant-month">{u?.month ?? "…"}</span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setMonth(shift(u?.month ?? "", 1))}
            aria-label={t("Next month")}
          >
            ›
          </button>
        </div>
      </header>
      {!u ? (
        <Skeleton rows={3} />
      ) : (
        <>
          <div className="card-body assistant-usage-figs">
            <div>
              <small className="muted">{t("On the company's key")}</small>
              <strong>{n(u.limitedTokens)}</strong>
              {u.monthlyLimit && (
                <small className="muted">
                  {t("of")} {n(u.monthlyLimit)} (
                  {Math.min(100, Math.round((u.limitedTokens / u.monthlyLimit) * 100))}%)
                </small>
              )}
            </div>
            <div>
              <small className="muted">{t("All tokens")}</small>
              <strong>{n(u.inputTokens + u.outputTokens)}</strong>
              <small className="muted">
                {n(u.inputTokens)} {t("in")} · {n(u.outputTokens)} {t("out")}
              </small>
            </div>
            <div>
              <small className="muted">{t("Cost")}</small>
              <strong>{priced ? money(u.cost ?? 0) : "—"}</strong>
              {!priced && <small className="muted">{t("Enter prices below to see it")}</small>}
            </div>
          </div>
          <DataTable
            rows={u.people}
            empty={t("Nobody has used the assistant this month.")}
            columns={[
              {
                key: "n",
                label: t("Person"),
                render: (p) => (
                  <span>
                    {p.name}
                    <span className="muted sm"> · {p.email}</span>
                  </span>
                ),
              },
              {
                key: "c",
                label: t("Conversations"),
                align: "right",
                hideSm: true,
                render: (p) => p.conversations,
              },
              { key: "t", label: t("Tokens"), align: "right", render: (p) => n(p.limitedTokens) },
              {
                key: "o",
                label: t("Own key"),
                align: "right",
                hideSm: true,
                render: (p) => (p.personalTokens ? n(p.personalTokens) : "—"),
              },
              ...(priced
                ? [
                    {
                      key: "m",
                      label: t("Cost"),
                      align: "right" as const,
                      render: (p: PersonUsage) => money(p.cost),
                    },
                  ]
                : []),
              {
                key: "l",
                label: t("Monthly limit"),
                render: (p) => (
                  <form
                    className="row assistant-limit"
                    onSubmit={(e) => {
                      e.preventDefault();
                      // What the field shows: typed, or the limit already in place.
                      const v = limitOf(own[p.userId] ?? (p.ownLimit ? String(p.ownLimit) : ""));
                      setLimit(p, v || null);
                    }}
                  >
                    <input
                      inputMode="numeric"
                      aria-label={t("{name}'s monthly limit", { name: p.name })}
                      placeholder={p.limit && !p.ownLimit ? `${n(p.limit)} (${t("default")})` : t("No limit")}
                      value={own[p.userId] ?? (p.ownLimit ? String(p.ownLimit) : "")}
                      onChange={(e) => setOwn((o) => ({ ...o, [p.userId]: e.target.value }))}
                    />
                    <button type="submit" className="btn btn-sm">
                      {t("Set")}
                    </button>
                    {p.ownLimit && (
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => setLimit(p, null)}
                      >
                        {t("Default")}
                      </button>
                    )}
                  </form>
                ),
              },
            ]}
          />
        </>
      )}
      <div className="card-body stack">
        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            saveLimits();
          }}
        >
          <Field
            label={t("Monthly limit for the company")}
            tip={t(
              "Tokens a month on the company's and the installation's key, for everyone together. When it is reached the assistant stops until next month, except for people using a key of their own. Empty: no limit.",
            )}
          >
            <input
              inputMode="numeric"
              placeholder={t("No limit")}
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </Field>
          <Field
            label={t("Monthly limit for each person")}
            tip={t(
              "The default for everyone; a person's own limit in the table above comes first. As a guide: each step re-reads the instructions and the conversation so far, so even a simple question takes several thousand tokens, and a long task with many look-ups or a large file can take a hundred thousand or more.",
            )}
          >
            <input
              inputMode="numeric"
              placeholder={t("No limit")}
              value={perPerson}
              onChange={(e) => setPerPerson(e.target.value)}
            />
          </Field>
          <div>
            <button type="submit" className="btn" disabled={busy}>
              {t("Save limits")}
            </button>
          </div>
        </form>
        <details>
          <summary>{t("Prices, to show cost")}</summary>
          <p className="muted sm">
            {t(
              "What your Anthropic account pays per million tokens, read in (input) and written (output), from your Anthropic plan or invoice. Keel doesn't assume a price.",
            )}
          </p>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              savePrices();
            }}
          >
            {s.models.map((m) => (
              <div key={m.id} className="row assistant-price">
                <span className="assistant-price-model">{m.label}</span>
                <input
                  inputMode="decimal"
                  aria-label={t("{model}: input per million", { model: m.label })}
                  placeholder={t("Input")}
                  value={prices[m.id]?.input ?? ""}
                  onChange={(e) =>
                    setPrices((p) => ({
                      ...p,
                      [m.id]: { input: e.target.value, output: p[m.id]?.output ?? "" },
                    }))
                  }
                />
                <input
                  inputMode="decimal"
                  aria-label={t("{model}: output per million", { model: m.label })}
                  placeholder={t("Output")}
                  value={prices[m.id]?.output ?? ""}
                  onChange={(e) =>
                    setPrices((p) => ({
                      ...p,
                      [m.id]: { input: p[m.id]?.input ?? "", output: e.target.value },
                    }))
                  }
                />
              </div>
            ))}
            <div className="row" style={{ gap: 8 }}>
              <Field label={t("Currency")}>
                <input
                  value={currency}
                  maxLength={3}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                />
              </Field>
              <button type="submit" className="btn" disabled={busy} style={{ alignSelf: "flex-end" }}>
                {t("Save prices")}
              </button>
            </div>
          </form>
        </details>
      </div>
    </section>
  );
}
